import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { TextInput, TocSmallText } from '@/components/widget-components'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEnrollmentHydration } from '@/hooks/useEnrollmentHydration'
import { useCorpStore } from '@/reducers/corporateReducer'
import { ROUTES } from '../CorporateSignupPanel'
import {
  RequestError,
  startCorporateEnrollment,
  updateCorporate,
} from '@/requests/corporate'

interface ValidationError {
  corporationName?: string
  corporateEmail?: string
  corporationNumber?: string
  corporatePhone?: string
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const validatePhone = (phone: string): boolean => {
  const digitsOnly = phone.replace(/\D/g, '')
  return digitsOnly.length === 10
}

const formatPhoneNumber = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '')

  if (digitsOnly.length >= 6) {
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`
  } else if (digitsOnly.length >= 3) {
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`
  }
  return digitsOnly
}

export function CorporateInfo() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const corporationName = useCorpStore((state) => state.corporationName)
  const setCorporationName = useCorpStore((state) => state.setCorporationName)
  const corporateEmail = useCorpStore((state) => state.corporateEmail)
  const { data: currentUser } = useCurrentUser(true)
  const setCorporateEmail = useCorpStore((state) => state.setCorporateEmail)
  const corporationNumber = useCorpStore((state) => state.corporationNumber)
  const setCorporationNumber = useCorpStore(
    (state) => state.setCorporationNumber
  )
  const corporatePhone = useCorpStore((state) => state.corporatePhone)
  const setCorporatePhone = useCorpStore((state) => state.setCorporatePhone)

  const [errors, setErrors] = useState<ValidationError>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  // New state to track if form is valid
  const [isFormValid, setIsFormValid] = useState(false)

  useEnrollmentHydration(
    useCallback(
      (enrollment) => {
        setCorporationName(enrollment.corporationName ?? '')
        setCorporateEmail(enrollment.corporationEmail ?? '')
        setCorporationNumber(enrollment.corporationNumber ?? '')
        setCorporatePhone(enrollment.phone ?? '')
      },
      [
        setCorporationName,
        setCorporateEmail,
        setCorporationNumber,
        setCorporatePhone,
      ]
    )
  )

  // Check form validity whenever fields change
  useEffect(() => {
    const formErrors = validateForm()
    const areAllFieldsFilled =
      corporationName.trim() !== '' &&
      corporateEmail.trim() !== '' &&
      corporationNumber.trim() !== '' &&
      corporatePhone.trim() !== ''

    setIsFormValid(Object.keys(formErrors).length === 0 && areAllFieldsFilled)
  }, [corporationName, corporateEmail, corporationNumber, corporatePhone])

  const validateForm = (): ValidationError => {
    const newErrors: ValidationError = {}

    if (!corporationName.trim()) {
      newErrors.corporationName = t('corporateInfo.errors.businessNameRequired')
    }

    if (!corporateEmail.trim()) {
      newErrors.corporateEmail = t('corporateInfo.errors.emailRequired')
    } else if (!validateEmail(corporateEmail)) {
      newErrors.corporateEmail = t('corporateInfo.errors.emailInvalid')
    } else if (
      currentUser?.email &&
      corporateEmail.trim().toLowerCase() === currentUser.email.toLowerCase()
    ) {
      newErrors.corporateEmail = t('corporateInfo.errors.emailSameAsPersonal')
    }

    if (!corporationNumber.trim()) {
      newErrors.corporationNumber = t(
        'corporateInfo.errors.businessNumberRequired'
      )
    }

    if (!corporatePhone.trim()) {
      newErrors.corporatePhone = t('corporateInfo.errors.phoneRequired')
    } else if (!validatePhone(corporatePhone)) {
      newErrors.corporatePhone = t('corporateInfo.errors.phoneInvalid')
    }

    return newErrors
  }

  const handleBlur = (field: keyof ValidationError) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const formErrors = validateForm()
    if (formErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: formErrors[field] }))
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value)
    setCorporatePhone(formattedPhone)
  }

  const handleSubmit = async () => {
    const formErrors = validateForm()
    setErrors(formErrors)
    setTouched({
      corporationName: true,
      corporateEmail: true,
      corporationNumber: true,
      corporatePhone: true,
    })

    if (Object.keys(formErrors).length === 0) {
      setIsSubmitting(true)
      setApiError(null)

      try {
        await startCorporateEnrollment({
          corporationName,
          corporationNumber,
          corporationEmail: corporateEmail.toLowerCase(),
        })

        await updateCorporate({
          corporationName,
          corporationNumber,
          corporationEmail: corporateEmail.toLowerCase(),
          phone: corporatePhone,
        })

        nextState()
        navigate(ROUTES.CORPORATE_DATE_DETAILS)
      } catch (error) {
        if (error instanceof RequestError && error.status === 409) {
          setErrors((prev) => ({
            ...prev,
            corporateEmail: t('corporateInfo.errors.emailTaken'),
          }))
        } else {
          setApiError(t('corporateInfo.errors.apiError'))
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }
  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && !isSubmitting) {
      e.preventDefault()
      await handleSubmit()
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCorporateEmail(e.target.value)
  }

  useEffect(() => {
    // Function to handle keydown events
    const handleGlobalKeyDown = async (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isFormValid && !isSubmitting) {
        e.preventDefault()
        await handleSubmit()
      }
    }

    // Add event listener when component mounts
    document.addEventListener('keydown', handleGlobalKeyDown)

    // Remove event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [isFormValid, isSubmitting]) // Dependencies ensure the listener updates when these values change

  return (
    <div
      className="flex flex-col gap-6"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-testid="container-corporate-info"
    >
      {' '}
      <div
        className="text-lg font-normal uppercase"
        data-testid="text-corporate-info-heading"
      >
        {t('corporateInfo.title')}
      </div>
      {apiError && (
        <div
          className="text-red-500 text-sm bg-red-50 p-3 rounded-md"
          data-testid="error-corporate-info-api"
        >
          {apiError}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <TextInput
          placeholder={t('corporateInfo.businessName.placeholder')}
          value={corporationName}
          maxLength={50}
          onChange={(e) => setCorporationName(e.target.value)}
          onBlur={() => handleBlur('corporationName')}
          data-testid="input-corporate-info-name"
        />
        {touched.corporationName && errors.corporationName && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="error-corporate-info-name"
          >
            {errors.corporationName}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <TextInput
          placeholder={t('corporateInfo.businessEmail.placeholder')}
          value={corporateEmail}
          type="email"
          maxLength={50}
          onChange={handleEmailChange}
          onBlur={() => handleBlur('corporateEmail')}
          data-testid="input-corporate-info-email"
        />
        <span
          className="text-xs text-gray-500"
          data-testid="text-corporate-info-email-hint"
        >
          {t('corporateInfo.emailHint')}
        </span>
        {touched.corporateEmail && errors.corporateEmail && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="error-corporate-info-email"
          >
            {errors.corporateEmail}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <TextInput
          placeholder={t('corporateInfo.businessNumber.placeholder')}
          value={corporationNumber}
          maxLength={50}
          onChange={(e) => setCorporationNumber(e.target.value)}
          onBlur={() => handleBlur('corporationNumber')}
          data-testid="input-corporate-info-number"
        />
        {touched.corporationNumber && errors.corporationNumber && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="error-corporate-info-number"
          >
            {errors.corporationNumber}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <TextInput
          placeholder={t('corporateInfo.phoneNumber.placeholder')}
          value={corporatePhone}
          type="tel"
          maxLength={15}
          onChange={handlePhoneChange}
          onBlur={() => handleBlur('corporatePhone')}
          data-testid="input-corporate-info-phone"
        />
        {touched.corporatePhone && errors.corporatePhone && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="error-corporate-info-phone"
          >
            {errors.corporatePhone}
          </span>
        )}
      </div>
      <TocSmallText data-testid="text-corporate-info-terms">
        {t('corporateInfo.termsText.part1')}
        <a
          href={import.meta.env.VITE_PAYTRIE_TOC_URL}
          target="_blank"
          style={{ textDecoration: 'underline' }}
          data-testid="link-corporate-info-terms"
        >
          {t('corporateInfo.termsText.termsLink')}
        </a>
        {t('corporateInfo.termsText.part2')}
        <a
          href={import.meta.env.VITE_PAYTRIE_PRIVACY_URL}
          target="_blank"
          style={{ textDecoration: 'underline' }}
          data-testid="link-corporate-info-privacy"
        >
          {t('corporateInfo.termsText.privacyLink')}
        </a>
        {t('corporateInfo.termsText.part3')}
        <a
          href={import.meta.env.VITE_KYC_TOC_LINK_URL}
          target="_blank"
          style={{ textDecoration: 'underline' }}
          data-testid="link-corporate-info-kyc-terms"
        >
          {t('corporateInfo.termsText.kycTermsLink')}
        </a>
        {t('corporateInfo.termsText.part4')}
        <a
          href={import.meta.env.VITE_KYC_TOC_PRIVACY_URL}
          target="_blank"
          style={{ textDecoration: 'underline' }}
          data-testid="link-corporate-info-kyc-privacy"
        >
          {t('corporateInfo.termsText.kycPrivacyLink')}
        </a>
        {t('corporateInfo.termsText.part5')}
      </TocSmallText>
      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
        className="mt-4 inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
        data-testid="btn-corporate-info-submit"
      >
        {isSubmitting
          ? t('corporateInfo.submitButton.saving')
          : t('corporateInfo.submitButton.continue')}
      </Button>
    </div>
  )
}

/*
Component Improvement Suggestions:
- The form currently uses manual validation. Consider using a form validation library like Formik or React Hook Form for more maintainable code.
- Error messages are repeated inline for each field. Consider creating a reusable error component.
- The component has many responsibilities (validation, API calls, state management). It could be split into smaller components.
- The email availability check could be debounced more efficiently with a custom hook.
- Consider adding more accessible form labels instead of relying solely on placeholders.
- The component could benefit from more comprehensive keyboard navigation support.
- Add proper aria-* attributes for better accessibility.
*/
